import { moduleNames } from "@lib/appconst"
import fileService from "@services/common/fileService"
import customerService from "@services/customer/customerService"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import { action, makeAutoObservable, observable } from "mobx"

class CustomerStore {
  @observable isLoading!: boolean
  @observable tableData!: PagedResultDto<any>
  @observable tableECIData!: any
  @observable auditLogData!: any
  @observable tableActivityData!: PagedResultDto<any>
  @observable tableEntityContactInfoData!: PagedResultDto<any>
  @observable entityContactInfoData!: any
  @observable detailData!: any

  constructor() {
    makeAutoObservable(this)

    this.tableData = { items: [], totalCount: 0 }
    this.tableECIData = []
    this.auditLogData = {}
    this.tableActivityData = { items: [], totalCount: 0 }
    this.tableEntityContactInfoData = { items: [], totalCount: 0 }
    this.entityContactInfoData = []
  }

  @action
  async createOrUpdate(body: any, files?: any) {
    if (!body.id) {
      this.isLoading = true
      const result = await customerService.create(body).catch(() => {
        this.isLoading = false
      })

      const { uniqueId } = result
      if (files && files.length && uniqueId) {
        files.forEach(async (file) => {
          if (file.originFileObj !== undefined) {
            const fileUpload = file.originFileObj
            await fileService
              .upload(moduleNames.customerAvatar, uniqueId, fileUpload)
              .catch(() => {
                this.isLoading = false
              })
          }
        })
        this.isLoading = false
      }
      this.isLoading = false
      this.detailData = result
      this.tableData.items.push(result)
    } else {
      this.isLoading = true
      const result = await customerService.update(body).catch(() => {
        this.isLoading = false
      })

      const { uniqueId } = result
      if (files && files.length && uniqueId) {
        files.forEach(async (file) => {
          if (file.originFileObj !== undefined) {
            const fileUpload = file.originFileObj
            await fileService
              .upload(moduleNames.customerAvatar, uniqueId, fileUpload)
              .catch(() => {
                this.isLoading = false
              })
          }
        })

        this.isLoading = false
      }
      this.isLoading = false
      this.tableData.items = this.tableData.items.map((x) => {
        if (x.id === body.id) x = result
        return x
      })
      return result
    }
  }

  @action
  async updateDirect(body: any, files?: any) {
    this.isLoading = true
    const result = await customerService.updateDirect(body).catch(() => {
      this.isLoading = false
    })

    const { uniqueId } = result
    if (files && files.length && uniqueId) {
      files.forEach(async (file) => {
        if (file.originFileObj !== undefined) {
          const fileUpload = file.originFileObj
          await fileService
            .upload(moduleNames.customerAvatar, uniqueId, fileUpload)
            .catch(() => {
              this.isLoading = false
            })
        }
      })
    }

    this.isLoading = false
    this.tableData.items = this.tableData.items.map((x) => {
      if (x.id === body.id) x = result
      return x
    })
    return result
  }

  @action
  async newCustomer() {
    this.detailData = {
      id: 0,
    }
  }

  @action
  async activateOrDeactivate(id: number, isActive) {
    await customerService.activateOrDeactivate(id, isActive)
  }
  @action
  async bulkActive(id: number[]) {
    await customerService.bulkActive(id)
  }
  @action
  async get(id: any, isViewFull: boolean = false) {
    this.isLoading = true
    const result = await customerService.get(id, isViewFull).finally(() => {
      this.isLoading = false
    })
    this.detailData = result
    return result
  }
  @action
  async getForMerge(id: number) {
    this.isLoading = true
    const result = await customerService.getForMerge(id).finally(() => {
      this.isLoading = false
    })
    return result
  }
  @action
  async getAll(params: any) {
    this.isLoading = true
    const result = await customerService
      .getAll(params)
      .finally(() => (this.isLoading = false))
    this.tableData = result
  }

  @action
  async getAllEntityContactInfo(params: any) {
    this.isLoading = true
    const result = await customerService
      .getAllEntityContactInfo(params)
      .finally(() => (this.isLoading = false))
    this.tableEntityContactInfoData = result
  }
  @action
  async createOrUpdateEntityContactInfo(body: any) {
    this.isLoading = true
    const result = await customerService
      .createOrUpdateEntityContactInfo(body)
      .finally(() => (this.isLoading = false))
    return result
  }
  @action
  async getEntityContactInfo(existingCustomerId: number) {
    this.isLoading = true
    const result = await customerService
      .getEntityContactInfo(existingCustomerId)
      .finally(() => (this.isLoading = false))
    this.entityContactInfoData = result
    return result
  }
  @action
  async getECI(params: any) {
    this.isLoading = true
    const result = await customerService
      .getECI(params)
      .finally(() => (this.isLoading = false))
    this.tableECIData = result
  }
  @action
  async mergeCustomer(params: any) {
    this.isLoading = true
    await customerService
      .mergeCustomer(params)
      .finally(() => (this.isLoading = false))
  }

  @action
  async assignUserInGroup(body: any) {
    this.isLoading = true
    const result = await customerService.assignUserInGroup(body).finally(() => {
      this.isLoading = false
    })
    return result
  }
  @action
  async removeUserInGroup(params: any) {
    this.isLoading = true
    const result = await customerService
      .removeUserInGroup(params)
      .finally(() => {
        this.isLoading = false
      })
    return result
  }
  @action
  public import = async (file: File) => {
    this.isLoading = true
    try {
      const result = await customerService
        .importMigration(file)
        .finally(() => (this.isLoading = false))

      return result
    } catch (error) {
      this.isLoading = false
      throw error
    }
  }

  @action
  async getAuditLog(id: number) {
    this.isLoading = true
    const result = await customerService.getAuditLog(id).finally(() => {
      this.isLoading = false
    })
    this.auditLogData = result
  }

  @action
  async getAllActivity(params: any) {
    this.isLoading = true
    const result = await customerService.getAllActivity(params).finally(() => {
      this.isLoading = false
    })
    this.tableActivityData = result
  }

  @action
  async getActivityById(id: number) {
    this.isLoading = true
    const result = await customerService.getActivityById(id).finally(() => {
      this.isLoading = false
    })
    return result
  }

  @action
  async createOrUpdateActivity(body: any) {
    this.isLoading = true
    const result = await customerService
      .createOrUpdateActivity(body)
      .finally(() => {
        this.isLoading = false
      })
    return result
  }

  @action
  async deleteActivity(id: number) {
    this.isLoading = true
    const result = await customerService.deleteActivity(id).finally(() => {
      this.isLoading = false
    })
    return result
  }

  @action
  async importEntityContactInfo(body: any) {
    this.isLoading = true
    const result = await customerService.importEntityContactInfo(body).finally(() => {
      this.isLoading = false
    })
    return result
  }

  @action
  async validateBeforeImportEntityContactInfo(file: File) {
    this.isLoading = true
    const result = await customerService.validateBeforeImportEntityContactInfo(file).finally(() => {
      this.isLoading = false
    })
    return result
  }
}

export default CustomerStore
